import { action, atom, withConcurrency } from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { makeActivityRequestAction } from '@root/entities/activity-action-type'
import { ActivityActionType } from '@root/entities/activity-action-type'
import { ActivityType } from '@root/entities/activity-action-type'
import { delugeIdAtom, userAtom } from '@root/entities/auth'
import {
  otherUserDelugeIdAtom,
  refreshUserProfileAction,
} from '@root/entities/profile'
import { initializeUserAction } from '@root/features/initialization/atoms/common/reatom-me'
import { reatomRequest } from '@root/shared/lib'

import type { Ctx } from '@reatom/framework'
import type { DelugeId } from '@root/entities/user-short'

export const reatomFollowingState = (options: { delugeId: DelugeId }) => {
  const isFollowingAtom = atom((ctx) => {
    // From the signed-in user rather than from the profile request: the
    // profile route was never ported, so the button used to sit on an error
    // and never knew its own state. `/me` carries the ids and is already
    // fetched on every visit.
    const user = ctx.spy(userAtom)
    const delugeId = ctx.spy(delugeIdAtom)

    if (delugeId === options.delugeId) {
      return AsyncStateN.success(undefined)
    }

    if (!user) {
      return AsyncStateN.loading()
    }

    const state: ActivityType.ActivityType = user.following.includes(
      options.delugeId,
    )
      ? ActivityType.following
      : ActivityType.notFollowing

    return AsyncStateN.success(state)
  }, 'isFollowingAtom')

  return { isFollowingAtom }
}

export const reatomActivityState = (options: { delugeId: DelugeId }) => {
  const { isFollowingAtom } = reatomFollowingState(options)
  const followingStateAtom = atom((ctx) => {
    const isFollowing = ctx.spy(isFollowingAtom)
    const activityState = ctx.spy(activityRequestStateAtom)

    return AsyncStateN.combineI([isFollowing, activityState], (x) => x)
  }, 'followingStateAtom')

  const activityRequestStateAtom = reatomRequest({
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'activityStateAtom',
    initial: AsyncStateN.success('-'), // doesn't matter
    execAction: action(async (ctx: Ctx) => {
      const followingState = ctx.get(isFollowingAtom)

      const nextActivityActionType: ActivityActionType.ActivityActionType =
        followingState.value
          ? ActivityActionType.forActivityType(followingState.value)
          : 'follow'

      const result = await ctx.schedule(() =>
        makeActivityRequestAction(ctx, {
          actionType: nextActivityActionType,
          targetDelugeId: options.delugeId,
        }),
      )

      if (Result.isSuccess(result)) {
        // Re-read who this account follows, which is what the button is.
        await ctx.schedule(() => initializeUserAction(ctx))

        const otherUserDelugeId = ctx.get(otherUserDelugeIdAtom)
        if (otherUserDelugeId === options.delugeId) {
          await ctx.schedule(() =>
            refreshUserProfileAction(ctx, options.delugeId),
          )
        }
      }
      return result
    }, 'activityStateAtom.execAction').pipe(withConcurrency()),
  })

  const changeActivityStateAction = activityRequestStateAtom.execute
  const refreshActivityStateAction = activityRequestStateAtom.refresh

  return {
    isFollowingAtom,
    followingStateAtom,
    changeActivityStateAction,
    refreshActivityStateAction,
  }
}
